import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

/**
 * Result of saving a task's details and then (if it changed) its status.
 * A failed *details* save is not represented here: it errors the stream, so the
 * status request is never sent.
 */
export interface TaskSaveResult {
  detailsResponse: any;
  statusFailed: boolean;
  statusError?: any;
}

/**
 * `PATCH tasks/{id}` ignores `status` (TaskController::getUpdateFormData), so a status picked in
 * the edit form has to go through `POST tasks/change-status/{id}`. Returns the id to send, or
 * null when the status was not changed (or no status is selected).
 */
export function resolveStatusChange(originalStatusId: any, selectedStatusId: any): number | null {
  if (selectedStatusId === null || selectedStatusId === undefined || selectedStatusId === '') {
    return null;
  }
  const selected = Number(selectedStatusId);
  if (!Number.isFinite(selected)) {
    return null;
  }
  return selected === Number(originalStatusId) ? null : selected;
}

/**
 * Saves the task details, then changes the status only when `statusId` is set. The status
 * request runs only after the details save succeeded; a status failure is reported in the
 * result (so the caller can say the details were saved) instead of failing the whole stream.
 */
export function saveTaskThenStatus(
  saveDetails: () => Observable<any>,
  statusId: number | null,
  changeStatus: (statusId: number) => Observable<any>
): Observable<TaskSaveResult> {
  return saveDetails().pipe(
    switchMap((detailsResponse) => {
      if (statusId === null) {
        return of<TaskSaveResult>({ detailsResponse, statusFailed: false });
      }
      return changeStatus(statusId).pipe(
        map((): TaskSaveResult => ({ detailsResponse, statusFailed: false })),
        catchError((statusError) =>
          of<TaskSaveResult>({ detailsResponse, statusFailed: true, statusError })
        )
      );
    })
  );
}
